import ButtonT from '@components/tailwind-components/Button/ButtonT';
import {
  TRAIN_AI_HOT_TOPIC_CONVERSATION_LISTING_ROUTE,
  TRAIN_AI_PAGE_ROUTE,
} from '@constants/routes';
import type { HotTopicWithRelatedDTO } from '@conversed/rest/src/contracts/platform-settings/platform-settings.dto';
import { purify } from '@helpers/domHelper';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import { useModal } from '@hooks/modal/useModal';
import { useHasTrainAIAccess } from '@hooks/useHasTrainAIAccess';
import { mainActions } from '@store/reducers/mainHandler';
import parse from 'html-react-parser';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

export const HotTopicRelatedQuestionsModal: React.FC<{
  topic: HotTopicWithRelatedDTO;
  hasBackNavigation?: boolean;
  onBack?: () => void;
}> = ({ topic, hasBackNavigation, onBack }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showTrainAILink = useHasTrainAIAccess();

  // Q&A content is always pre-loaded by the caller before this modal opens.
  // From the dashboard: conversations arrive in the API response.
  // From the fact-checking flow: handleViewRelatedConversations fetches before opening.
  const items = topic.conversations ?? [];
  const hasItems = items.length > 0;

  const handleTrainAI = useCallback(() => {
    const conversationIds = items.map((conv) => conv._id);
    dispatch(mainActions.setHotTopicConversationIds(conversationIds));
    dispatch(mainActions.setUIState({ isLoading: true }));
    hideModal();
    navigate({
      pathname: `${TRAIN_AI_PAGE_ROUTE}${TRAIN_AI_HOT_TOPIC_CONVERSATION_LISTING_ROUTE}`,
    });
  }, [dispatch, hideModal, navigate, items]);

  const handleBack = useCallback(() => {
    hideModal();
    if (onBack) {
      onBack();
    }
  }, [hideModal, onBack]);

  return (
    <div className='tailwind-modal-form flex-1 px-2'>
      <div className='tailwind-modal-body flex flex-col border border-primary-base rounded-md bg-gray-100 px-0 py-0 max-h-[70vh] overflow-hidden'>
        <div className='flex bg-primary-dark text-white text-sm font-normal py-1 px-3 items-center'>
          <span className='font-medium'>
            {getHotTopicDisplayName(topic.name)}
          </span>
          {topic.percentage > 0 && (
            <span className='ml-1'>({topic.percentage}%)</span>
          )}
          <span className='ml-1'>
            | {items.length}{' '}
            {t('dashboard.trends.hot-topics.related-questions')}
          </span>
        </div>
        <div className='flex-1 overflow-y-auto p-3 whitespace-pre-wrap word-break-style min-h-[100px]'>
          {hasItems ? (
            <div className='space-y-2 text-xs'>
              {items.map((item, index) => (
                <div key={item._id}>
                  <div className='font-medium mb-1 text-primary-dark'>
                    ❓: {item.question}
                  </div>
                  <div className='font-normal text-gray-600 formatted-html-content'>
                    🤖: {parse(purify(item.answer))}
                  </div>
                  {index < items.length - 1 && (
                    <div className='tailwind-divider-x' />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className='flex h-[calc(100px-24px)] items-center justify-center text-sm font-normal text-gray-500 italic'>
              {t('dashboard.trends.hot-topics.no-related-content')}
            </div>
          )}
        </div>
      </div>

      <div className='tailwind-modal-footer px-0 flex justify-between'>
        <div className='flex gap-2'>
          {hasBackNavigation && (
            <ButtonT
              onClick={handleBack}
              type='button'
              styling='secondary'
              size='xs'
              rounding='md'>
              {t('general.buttons.back')}
            </ButtonT>
          )}
        </div>

        <div className='flex gap-2'>
          <ButtonT
            onClick={hideModal}
            type='button'
            styling='cancel'
            size='xs'
            rounding='md'>
            {t('general.buttons.close')}
          </ButtonT>

          {hasItems && showTrainAILink && (
            <ButtonT
              onClick={handleTrainAI}
              type='button'
              styling='primary'
              size='xs'
              rounding='md'>
              {t('dashboard.trends.hot-topics.train-ai')}
            </ButtonT>
          )}
        </div>
      </div>
    </div>
  );
};
